import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { DELIVERY_STATUS, deliveryLabel } from "@/lib/format";
import { Section, Eyebrow, Timeline, EmptyState } from "@/components/domain/Bits";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, CheckCircle2, MapPin, Calendar, Quote, ArrowLeft } from "lucide-react";

export function Winners() {
  const [winners, setWinners] = useState([]);
  const [year, setYear] = useState("all");
  useEffect(() => { api.get("/winners").then((r) => setWinners(r.data)); }, []);
  const years = [...new Set(winners.map((w) => w.year))].sort((a, b) => b - a);
  const filtered = winners.filter((w) => year === "all" ? true : w.year === Number(year));

  return (
    <div>
      <div className="trust-gradient text-white" data-testid="winners-hero">
        <Section className="py-16">
          <span data-testid="winners-eyebrow" className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-bright"><Trophy className="h-3.5 w-3.5" />Casos reales</span>
          <h1 data-testid="winners-heading" className="mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl">Ganadores <span className="gold-text">verificables</span></h1>
          <p data-testid="winners-description" className="mt-4 max-w-2xl text-lg text-slate-300">Cada ganador con perfil público, número ganador y evidencia de entrega. Datos de demostración.</p>
        </Section>
      </div>
      <Section className="py-12">
      <Tabs value={year} onValueChange={setYear} className="mt-2" data-testid="winners-year-tabs">
        <TabsList>
          <TabsTrigger value="all" data-testid="winners-year-all">Todos</TabsTrigger>
          {years.map((y) => <TabsTrigger key={y} value={String(y)} data-testid={`winners-year-${y}`}>{y}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <EmptyState icon={Trophy} title="Sin ganadores en este periodo" />
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((w, i) => (
            <motion.div key={w.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link to={`/ganadores/${w.id}`} data-testid={`winner-card-${w.id}`} className="group block overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-xl">
                <div className="aspect-[4/3] overflow-hidden"><img src={w.gallery?.[0]} alt={w.name_public} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                <div className="p-5">
                  <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${DELIVERY_STATUS[w.delivery_status] || DELIVERY_STATUS.Pendiente}`}><CheckCircle2 className="h-3 w-3" />{deliveryLabel(w)}</div>
                  <h3 className="mt-3 font-bold text-slate-900">{w.name_public}</h3>
                  <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{w.city}</p>
                  <p className="mt-2 text-sm font-medium text-slate-700">{w.prize}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-mono text-sm font-semibold text-brand">#{w.winning_number}</span>
                    <span className="text-xs text-slate-400">{w.year}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
      </Section>
    </div>
  );
}

export function WinnerProfile() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  useEffect(() => { api.get(`/winners/${id}`).then((r) => setW(r.data)); }, [id]);
  if (!w) return <Section className="py-20"><div className="h-96 animate-pulse rounded-xl bg-slate-100" /></Section>;

  return (
    <Section className="py-10">
      <Link to="/ganadores" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand"><ArrowLeft className="h-4 w-4" />Volver a ganadores</Link>
      <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr]">
        <div>
          <div className="overflow-hidden rounded-2xl border border-slate-200"><img src={w.gallery?.[0]} alt={w.name_public} className="aspect-[16/10] w-full object-cover" /></div>
          {w.gallery?.length > 1 && (
            <div className="mt-3 grid grid-cols-3 gap-3">{w.gallery.slice(1).map((g, i) => <img key={i} src={g} alt="" className="aspect-video w-full rounded-lg object-cover" />)}</div>
          )}
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
            <Quote className="h-6 w-6 text-brand" />
            <p className="mt-3 text-lg italic text-slate-700">"{w.testimonial}"</p>
            <p className="mt-3 text-sm font-semibold text-slate-900">— {w.name_public}, {w.city}</p>
            <p className="mt-1 text-xs text-amber-600">Testimonio de demostración.</p>
          </div>
        </div>
        <div>
          <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${DELIVERY_STATUS[w.delivery_status] || DELIVERY_STATUS.Pendiente}`}><CheckCircle2 className="h-3.5 w-3.5" />{deliveryLabel(w)}</div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">{w.name_public}</h1>
          <p className="mt-1 flex items-center gap-1.5 text-slate-500"><MapPin className="h-4 w-4" />{w.city}</p>
          <div className="mt-6 rounded-2xl bg-slate-900 p-6 text-white">
            <div className="text-xs text-slate-400">Número ganador</div>
            <div className="font-mono text-5xl font-bold">#{w.winning_number}</div>
          </div>
          <dl className="mt-6 space-y-3 rounded-xl border border-slate-200 bg-white p-6 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Premio</dt><dd className="font-semibold text-slate-900">{w.prize}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Sorteo</dt><dd className="font-medium text-slate-900">{w.raffle_name}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500"><Calendar className="mr-1 inline h-3.5 w-3.5" />Fecha</dt><dd className="font-medium text-slate-900">{w.date}</dd></div>
          </dl>
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
            <h3 className="mb-5 font-semibold text-slate-900">Proceso de entrega</h3>
            <Timeline items={[{ label: "Sorteo realizado", done: true }, { label: "Ganador confirmado", done: true }, { label: "Contacto con el ganador", done: true }, { label: "Premio entregado", done: true }]} />
          </div>
        </div>
      </div>
    </Section>
  );
}
